// src/features/form/FormTypesSettings.tsx
// Admin: product form-type registry. Products normally call RegisterFormType
// on startup; this tab is the admin escape hatch to inspect/adjust/deactivate.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useFormFormType, useI18n } from '@react/hooks';
import { Alert, Badge, Button, Input, Textarea } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import type { FormTypeDefinition } from '@core/types/form';
import { FormModalShell } from './FormModalShell';
import { errMessage, makeEntity } from './helpers';

export const FormTypesSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useFormFormType();

  const [types, setTypes] = useState<FormTypeDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FormTypeDefinition | null>(null);
  const [deleting, setDeleting] = useState<FormTypeDefinition | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [productId, setProductId] = useState('');
  const [sourceEntityType, setSourceEntityType] = useState('');

  const load = () => {
    setIsLoading(true);
    setError(null);
    store
      .ListFormTypes()
      .then((res) => setTypes(res.definitions ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('form.types.loadError'))))
      .finally(() => setIsLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = async (listed: FormTypeDefinition | null) => {
    let def = listed;
    if (listed?.entity?.id) {
      try {
        const res = await store.GetFormType(listed.entity.id);
        def = res.definition ?? listed;
      } catch {
        def = listed;
      }
    }
    setEditing(def);
    setSlug(def?.slug ?? '');
    setDisplayName(def?.display_name ?? '');
    setDescription(def?.description ?? '');
    setProductId(def?.product_id ?? '');
    setSourceEntityType(def?.source_entity_type ?? '');
    setDialogOpen(true);
  };

  const save = async () => {
    if (!slug.trim() || !displayName.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (editing?.entity?.id) {
        await store.UpdateFormType(editing.entity.id, {
          definition: { ...editing, display_name: displayName.trim(), description },
          update_mask: ['display_name', 'description'],
        });
      } else {
        await store.RegisterFormType({
          entity: makeEntity('', 'form_type_definition', scope),
          definition: {
            entity: makeEntity('', 'form_type_definition', scope),
            slug: slug.trim(),
            display_name: displayName.trim(),
            description,
            product_id: productId.trim(),
            source_entity_type: sourceEntityType.trim(),
            auto_create_form: false,
            require_submission_approval: false,
            enable_draft_save: true,
            enable_file_uploads: true,
            enable_conditional_logic: true,
            enable_analytics: true,
            enable_notifications: true,
            source_created_topic: '',
            source_updated_topic: '',
            source_deleted_topic: '',
            is_active: true,
          },
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('form.types.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('form.types.hint')}</p>
        <Button size="sm" onClick={() => openDialog(null)}>{t('form.types.newType')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {isLoading && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!isLoading && types.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('form.types.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {types.map((def) => (
          <div key={def.entity?.id ?? def.slug} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{def.display_name}</span>
                <Badge variant="default">{def.slug}</Badge>
                {!def.is_active && <Badge variant="warning">{t('form.types.inactive')}</Badge>}
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">{def.source_entity_type} · {def.product_id}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => openDialog(def)}>{t('common.edit')}</Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(def)}>{t('common.delete')}</Button>
            </div>
          </div>
        ))}
      </div>

      <FormModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('form.types.editType') : t('form.types.newType')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={isSaving}>{t('common.cancel')}</Button>
            <Button size="sm" onClick={save} isLoading={isSaving} disabled={!slug.trim() || !displayName.trim()}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label={t('form.types.slug')} value={slug} onChange={(e) => setSlug(e.target.value)} disabled={!!editing} required />
          <Input label={t('form.types.displayName')} value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          <Textarea label={t('common.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          {!editing && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input label={t('form.types.productId')} value={productId} onChange={(e) => setProductId(e.target.value)} />
              <Input label={t('form.types.sourceEntityType')} value={sourceEntityType} onChange={(e) => setSourceEntityType(e.target.value)} />
            </div>
          )}
        </div>
      </FormModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('form.types.deleteTitle')}
        description={t('form.types.deleteDescription')}
        destructive
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteFormType(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('form.types.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
